import React from 'react';
import { cn } from '@/lib/cn';
import { Accordion } from '@/components/ui/Accordion';
import { useStore } from '@/providers/StoreProvider';
import type { Neighborhood } from '@repo/api';
import { Divider } from '@/components/ui';
import NeighborhoodItem from './NeighborhoodItem';
import AllBoroughOption from './AllBoroughOption';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface NeighborhoodAccordionProps {
  className?: string;
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  resetBuildingOnSelect?: boolean;
}

type ExtendedNeighborhood = Neighborhood & { parentId?: string | null };

export function NeighborhoodAccordion({
  className,
  selectedNeighborhoodIds: propSelectedIds,
  onToggleNeighborhood: propToggleNeighborhood,
  onToggleNeighborhoodWithChildren: propToggleNeighborhoodWithChildren,
  onToggleBorough: propToggleBorough,
  resetBuildingOnSelect = true,
}: NeighborhoodAccordionProps) {
  const analytics = useAnalytics();
  const neighborhoodAccordionData = useStore(s => s.neighborhoods.neighborhoodAccordionData);
  const storeNeighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const storeToggleNeighborhood = useStore(s => s.listingsFilters.toggleNeighborhood);
  const storeToggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );
  const storeToggleBorough = useStore(s => s.listingsFilters.toggleBorough);

  // Use props if provided, otherwise fall back to store
  const neighborhoodIds = propSelectedIds ?? storeNeighborhoodIds;
  const toggleNeighborhood = propToggleNeighborhood ?? storeToggleNeighborhood;
  const toggleNeighborhoodWithChildren =
    propToggleNeighborhoodWithChildren ?? storeToggleNeighborhoodWithChildren;
  const toggleBorough = propToggleBorough ?? storeToggleBorough;
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
  const sortByName = (a: { name: string }, b: { name: string }) => collator.compare(a.name, b.name);
  const sortByText = (a: string, b: string) => collator.compare(a, b);

  const getOrganizedNeighborhoods = (borough: string) => {
    const entry = neighborhoodAccordionData?.[borough];
    return entry || { parents: [], childrenByParent: {} as Record<string, ExtendedNeighborhood[]> };
  };

  const boroughs = Object.keys(neighborhoodAccordionData || {}).sort(sortByText);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {boroughs.map((borough, boroughIndex) => {
        const entry = neighborhoodAccordionData[borough];
        if (!entry) return null;

        const { parents, childrenByParent } = getOrganizedNeighborhoods(borough);
        const sortedParents = [...parents].sort(sortByName);
        const sortedChildrenByParent = Object.fromEntries(
          Object.entries(childrenByParent).map(([parentId, children]) => [
            parentId,
            [...children].sort(sortByName),
          ]),
        ) as typeof childrenByParent;

        const allIdsInBorough = [
          ...entry.parents.map(p => p.id),
          ...Object.values(entry.childrenByParent)
            .flat()
            .map(c => c.id),
        ];

        const allSelected =
          Array.isArray(neighborhoodIds) && allIdsInBorough.length > 0
            ? allIdsInBorough.every(id => neighborhoodIds.includes(id))
            : false;

        return (
          <React.Fragment key={borough}>
            {boroughIndex > 0 && <Divider variant="tertiary" />}
            <Accordion
              header={
                <span className="text-body-2-high text-primary font-semibold">{borough}</span>
              }
              headerClassName={cn(
                'flex items-center gap-4',
                'px-2 py-3',
                'rounded-xl',
                'bg-primary hover:bg-primary-hover',
                'text-body-2-high text-primary font-semibold',
              )}
              contentClassName="px-0 pt-0 pb-0"
            >
              <div className="flex flex-col gap-1">
                <AllBoroughOption
                  borough={borough}
                  isSelected={allSelected}
                  onToggle={() => {
                    analytics.track(
                      allSelected
                        ? 'listings_filter_borough_unselect'
                        : 'listings_filter_borough_select',
                      { boroughName: borough },
                    );
                    if (resetBuildingOnSelect) resetBuilding();
                    toggleBorough(borough);
                  }}
                />

                {sortedParents.map(parent => {
                  const isParentSelected =
                    Array.isArray(neighborhoodIds) && neighborhoodIds.includes(parent.id);
                  const nestedChildren = sortedChildrenByParent[parent.id] || [];

                  return (
                    <React.Fragment key={parent.id}>
                      <NeighborhoodItem
                        neighborhood={parent}
                        isSelected={isParentSelected}
                        onToggle={() => {
                          analytics.track(
                            isParentSelected
                              ? 'listings_filter_neighborhood_unselect'
                              : 'listings_filter_neighborhood_select',
                            { neighborhoodId: parent.id, neighborhoodName: parent.name },
                          );
                          if (resetBuildingOnSelect) resetBuilding();
                          toggleNeighborhoodWithChildren(parent.id);
                        }}
                        isNested={false}
                      />
                      {nestedChildren.map(child => {
                        const isChildSelected =
                          Array.isArray(neighborhoodIds) && neighborhoodIds.includes(child.id);
                        return (
                          <NeighborhoodItem
                            key={child.id}
                            neighborhood={child}
                            isSelected={isChildSelected}
                            onToggle={() => {
                              analytics.track(
                                isChildSelected
                                  ? 'listings_filter_neighborhood_unselect'
                                  : 'listings_filter_neighborhood_select',
                                { neighborhoodId: child.id, neighborhoodName: child.name },
                              );
                              if (resetBuildingOnSelect) resetBuilding();
                              toggleNeighborhood(child.id);
                            }}
                            isNested={true}
                          />
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </Accordion>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default NeighborhoodAccordion;
